import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getWorkshopContents, getUnits, createWorkshopContent } from '@/lib/db'
import type { WorkshopContent, Unit } from '@/types'
import {
  Presentation,
  Loader2,
  Pencil,
  CheckCircle2,
  XCircle,
  Layers,
  PlayCircle,
  Wand2
} from 'lucide-react'

export default function WorkshopContentList() {
  const [contents, setContents] = useState<WorkshopContent[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    async function load() {
      setLoading(true)
      const [contentsRes, unitsRes] = await Promise.all([
        getWorkshopContents(),
        getUnits()
      ])
      if (contentsRes.data) setContents(contentsRes.data)
      if (unitsRes.data) setUnits(unitsRes.data)
      setLoading(false)
    }
    load()
  }, [])

  // Unidades que aún no tienen contenido de taller
  const usedUnitIds = new Set(contents.map(c => c.unit_id))
  const availableUnits = units.filter(u => !usedUnitIds.has(u.id))

  const handleCreate = async (unitId: string) => {
    const unit = units.find(u => u.id === unitId)
    if (!unit) return

    setCreating(true)
    setError('')
    try {
      const unitTitle = (unit as any).name_es || (unit as any).title || `Unidad ${(unit as any).unit_number}`
      const { data, error: createError } = await createWorkshopContent({
        unit_id: unitId,
        title: `Taller en vivo - ${unitTitle}`,
        estimated_duration_minutes: 45
      })
      if (createError) {
        console.error('Error creating workshop content:', createError)
        const msg = (createError as any)?.message || 'Error al crear'
        if (msg.includes('unique') || msg.includes('duplicate')) {
          setError('Ya existe material para esa unidad. Recarga la página.')
        } else {
          setError(msg)
        }
        return
      }
      if (data) setContents(prev => [...prev, data])
    } finally {
      setCreating(false)
    }
  }

  const getUnitName = (content: WorkshopContent) => {
    const unit = content.unit as any
    return unit?.name_es || unit?.title || 'Sin unidad'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Presentation className="w-7 h-7 text-purple-500" />
            Material de talleres
          </h1>
          <p className="text-gray-500 mt-1">
            Contenido interactivo para talleres en vivo (1 por unidad)
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/admin/workshop-content/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
          >
            <Wand2 className="w-4 h-4" />
            Crear taller
          </Link>
        </div>

        {availableUnits.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              id="unit-select"
              className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) handleCreate(e.target.value)
              }}
              disabled={creating}
            >
              <option value="" disabled>Crear para unidad...</option>
              {availableUnits.map(u => (
                <option key={u.id} value={u.id}>
                  {(u as any).name_es || (u as any).title || `Unidad ${(u as any).unit_number}`}
                </option>
              ))}
            </select>
            {creating && <Loader2 className="w-4 h-4 animate-spin text-[#0055A4]" />}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Grid de talleres */}
      {contents.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Presentation className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">Sin material de talleres</p>
          <p className="text-gray-500 mt-1">
            Selecciona una unidad arriba para crear el primer taller
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contents.map(content => {
            const blockCount = (content as any).blocks?.length || 0
            return (
              <div
                key={content.id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Layers className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{content.title}</h3>
                      <p className="text-sm text-gray-500">{getUnitName(content)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {content.is_published ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle2 className="w-3 h-3" />
                        Publicado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                        <XCircle className="w-3 h-3" />
                        Borrador
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{blockCount} bloque{blockCount !== 1 ? 's' : ''}</span>
                    <span>{content.estimated_duration_minutes} min</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => window.open(`/admin/workshop-content/${content.id}/present`, '_blank', 'noopener')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                      <PlayCircle className="w-3.5 h-3.5" />
                      Proyectar
                    </button>
                    <Link
                      to={`/admin/workshop-content/${content.id}/edit`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Editar
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {contents.length > 0 && contents.length < 4 && availableUnits.length > 0 && (
        <p className="text-sm text-gray-500 text-center">
          {contents.length} de 4 talleres creados. Selecciona una unidad arriba para crear los restantes.
        </p>
      )}
    </div>
  )
}
